import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { Helmet } from 'react-helmet'

// Actions
import { bankActions, dealActions, notificationsActions } from '../_actions'
import { useModal } from '../_reduxHooks/modal.hook'

// Components
import { Button } from '../_components/Button'
import Tabs from '../_components/Tabs'
import { OverviewStats } from '../_components/OverviewStats'
import { Submit } from '../_components/modals/clientHome/Submit'
import { DownloadReportsTable } from '../_components/DownloadReportsTable'
import { SearchableOverview } from '../_components/SearchableOverview'
import { ISTable, ISTableColumns } from '../_views/LenderViews/ISTable'
import ClientInvoiceTable from '../_views/LenderViews/ClientInvoiceTable'
import { ClientFundingRequest } from '../_components/modals/client_funding_request/ClientFundingRequest'
import { Table } from '../_components/Table'
import { createColumn } from '../_components/TableColumn'
import { BusinessPaymentsTable, BusinessPaymentsTableColumns } from './BusinessPaymentsTable'

// Constants
import { clientHomeConfig } from '../_configs'
import { bankConstants } from '../_constants/bank.constants'
import ROUTES from '../_constants/routes'
import { FundingRequestColumns } from '../_views/LenderViews/FundingRequestOverview/FundingRequestsTable.jsx'

// Services
import { getPendingInvoices } from '../_services/invoices.service'
import {
  getInvoiceSchedulesV2,
  getBorrowerDebtorPayments,
  getBorrowerDebtorPaymentsCount,
  getFundingRequests,
  getFundingRequestsCount,
  getInvoiceSchedules,
  getInvoiceSchedulesCount,
} from '../_services/lender.service'
import { getReports } from '../_services/report.service'
import { businessService } from '../_services'

// Helpers
import { history } from '../_helpers'

// Styled Components
import { S_ClientHomePage } from './S_ClientHomePage'
import { isFeatureEnabled } from '../_helpers/flags'
import { logError } from '../_helpers/errors'
import { ClientCreateScheduleModal } from '../_components/modals/scheduleInvoices/ClientCreateScheduleModal'
import { CREATE_NEW_SCHEDULE_TITLE } from '../_components/modals/scheduleInvoices/constants'
import { COMPANY_NAME } from 'xen/constants'
import { PlaidAccounts, PlaidLink } from 'xen/_modules/linked-external-accounts'
import { Stack } from 'xen-ui'

const ClientHomePage = (props) => {
  const [reports, setReports] = useState([])
  const [additionalStats, setAdditionalStats] = useState(null)
  const { openModal } = useModal()
  const [frCols, setFrCols] = useState([])
  const [reloadFundingRequests, setReloadFundingRequests] = useState()
  const [loading, setLoading] = useState(true)
  const [selectedAccount, setSelectedAccount] = useState(null)

  const { tenant, user, business, featureFlags, getDeal } = props

  useEffect(() => {
    const fetchDeal = async () => {
      await getDeal(business.id)
      Promise.all([getAvailableReports(), getAdditionalStats()]).then(() => {
        const statusCol = FundingRequestColumns.find((col) => col.accessor === 'last_approval')
        const amtCol = FundingRequestColumns.find((col) => col.id === 'amount')
        const fundedCol = FundingRequestColumns.find((col) => col.id === 'funded')
        const createdAtCol = createColumn({
          type: 'date',
          id: 'created_at',
          headingText: 'Date Requested',
        })
        setFrCols([statusCol, createdAtCol, amtCol, fundedCol].map((col) => ({ ...col, disableSortBy: true })))
        setLoading(false)
      })
    }

    fetchDeal()
  }, [])

  const getAdditionalStats = async () => {
    businessService
      .getLedgerSummary(props.business.id)
      .then((summary) => {
        for (let key in summary) {
          if (key !== 'nfe_to_invoices') {
            summary[key].value = Number(summary[key].amount)
            summary[key].amount = Number(summary[key].amount)
          }
        }
        delete summary.nfe_to_invoices
        setAdditionalStats(summary)
      })
      .catch((err) => {
        logError(err)
      })
  }

  const getAvailableReports = async () => {
    getReports(true).then((clientReports) => {
      setReports(clientReports)
    })
  }

  const uploadInvoicesActionvalidation = async () => {
    const { tenant, business } = props
    const isNewInvoiceRedesign = isFeatureEnabled('new_invoice_redesign', featureFlags)

    if (!clientHomeConfig[tenant].details.legalese) {
      isNewInvoiceRedesign
        ? openModal(
            ClientCreateScheduleModal,
            { business },
            { modalTitle: CREATE_NEW_SCHEDULE_TITLE, titleAlign: 'center' }
          )
        : history.push(ROUTES.client_invoice_submission + `/create`)
    } else {
      openModal(() => {
        return <Submit business={business} onSubmit={getPendingInvoices} />
      })
    }
  }

  const config = clientHomeConfig[tenant]

  const isBankIntegrationEnabled =
    isFeatureEnabled('xen_premium', featureFlags) && isFeatureEnabled('banking_integration', featureFlags)
  const isFundingRequestForBorrowerEnabled = isFeatureEnabled('funding_for_borrower', featureFlags)

  if (loading) {
    return null
  }

  return (
    <S_ClientHomePage>
      <Helmet>
        <title>Client Home • {COMPANY_NAME}</title>
      </Helmet>
      <div className={`main-content`}>
        {/* Header */}
        <div className={`flex-header`}>
          <h1 className="business-name">{business.business_name || user.email_address}</h1>
        </div>
        <SearchableOverview
          searchPlaceholder="Search Invoice Schedules"
          showSearchBar={false}
          views={[
            {
              title: 'Incomplete Schedules',
              endpointCall: async (searchParams) => {
                searchParams.set('state', 'incomplete')
                return await getInvoiceSchedulesV2(searchParams)
              },
              requestMethod: 'GET',
              defaultSort: [{ id: 'updated_at', desc: true }],
              columns: ISTableColumns.filter((col) => col.accessor != 'business_name'),
              Component: ({ results, defaultSort, handleSortChange, pageSize, columns }) => {
                return (
                  <ISTable
                    columns={columns}
                    defaultSort={defaultSort}
                    handleSortChange={handleSortChange}
                    invoiceSchedules={results}
                    mobileScroll={true}
                    pageSize={pageSize}
                    scheduleBaseRoute={ROUTES.client_invoice_submission}
                  />
                )
              },
            },
          ]}
        />
        {/* Actions */}
        <div className="actions-row">
          <Button
            className={`submit-btn`}
            onClick={uploadInvoicesActionvalidation}
            text={config.details.submit_btn_label}
            type={`button`}
          />

          {isFundingRequestForBorrowerEnabled ? (
            <Button
              onClick={() => {
                openModal(ClientFundingRequest, { business, onCreate: reloadFundingRequests })
              }}
              text={'Request Funding'}
            />
          ) : null}
        </div>

        {isBankIntegrationEnabled && (
          <section className={'linked-bank-accounts'}>
            <h3 className={'section-title'}>Financials</h3>
            <div className={'section-content'}>
              <Stack>
                <PlaidLink entityId={business.id} entityType="business" />
                <PlaidAccounts
                  entityId={business.id}
                  entityType="business"
                  onAccountSelected={setSelectedAccount}
                  selectedAccount={selectedAccount}
                />
              </Stack>
            </div>
          </section>
        )}

        {/* Overview */}
        <section className={`overview`}>
          <h3 className={`section-title`}>{config.overview.title}</h3>
          <div className={`section-content`}>
            <OverviewStats
              additional_stats={additionalStats}
              stats={[
                {
                  type: 'currency',
                  name: 'outstanding_invoices',
                  label: 'Outstanding Invoices',
                },
                {
                  type: 'currency',
                  name: 'escrow_reserve',
                  label: 'Escrow Reserves',
                },
                {
                  type: 'currency',
                  name: 'client_reserve',
                  label: 'Client Reserves',
                },
                {
                  type: 'currency',
                  name: 'net_funds_employed',
                  label: 'Current Net Funds Employed',
                },
                {
                  type: 'percent',
                  name: 'advance_rate',
                  label: 'Advance Rate',
                },
              ]}
            />
          </div>
        </section>

        <section className={`details`}>
          <h3 className={`section-title`}>Details</h3>
          <div className={`client-reports no-spacing`}>
            <Tabs bordered={true}>
              <div label="Open Invoices">
                <ClientInvoiceTable businessId={business.id} type="open" />
              </div>
              <div label="All Invoice Schedules">
                <SearchableOverview
                  searchPlaceholder="Search Invoice Schedules"
                  showSearchBar={false}
                  views={[
                    {
                      title: '',
                      endpointCall: (searchParams) => {
                        const alreadySorted = searchParams.has('sort_by')
                        if (!alreadySorted) {
                          searchParams.set('sort_by', 'updated_at')
                          searchParams.set('sort_asc', false)
                        }

                        const finalParams = searchParams.toString()
                        return Promise.all([
                          getInvoiceSchedules(finalParams),
                          getInvoiceSchedulesCount(finalParams),
                        ]).then((values) => ({
                          results: values[0].map((invoice_schedule) => {
                            if (invoice_schedule.state.toLowerCase() === 'funded') {
                              invoice_schedule.state = 'purchased'
                            }
                            return invoice_schedule
                          }),
                          total_results: values[1].count,
                        }))
                      },
                      requestMethod: 'GET',
                      columns: ISTableColumns.filter((col) => col.accessor != 'business_name'),
                      Component: ({ results, sort, handleSortChange, pageSize, columns }) => {
                        return (
                          <ISTable
                            columns={columns}
                            handleSortChange={handleSortChange}
                            invoiceSchedules={results}
                            pageSize={pageSize}
                            scheduleBaseRoute={ROUTES.client_invoice_submission}
                            sorted={sort}
                          />
                        )
                      },
                    },
                  ]}
                />
              </div>
              <div label="Funding Requests">
                <SearchableOverview
                  searchPlaceholder="Search Invoice Schedules"
                  showSearchBar={false}
                  views={[
                    {
                      title: '',
                      endpointCall: (searchParams) => {
                        const alreadySorted = searchParams.has('sort_by')
                        if (!alreadySorted) {
                          searchParams.set('sort_by', 'created_at')
                          searchParams.set('sort_asc', false)
                        }
                        searchParams.set('business_id', business.id)
                        const finalParams = searchParams.toString()
                        return Promise.all([
                          getFundingRequests(finalParams),
                          getFundingRequestsCount(finalParams),
                        ]).then((values) => ({
                          results: values[0],
                          total_results: values[1],
                        }))
                      },
                      requestMethod: 'GET',
                      columns: frCols,
                      fetchReload: (fn) => setReloadFundingRequests(() => fn),
                      Component: ({ results, sort, handleSortChange, pageSize, columns }) => {
                        return (
                          <Table
                            columns={columns}
                            data={results}
                            handleSortChange={handleSortChange}
                            pageSize={pageSize}
                            sorted={sort}
                          />
                        )
                      },
                    },
                  ]}
                />
              </div>
              <div label="Payments">
                <SearchableOverview
                  showSearchBar={false}
                  views={[
                    {
                      title: '',
                      endpointCall: (searchParams) => {
                        const alreadySorted = searchParams.has('sort_by')
                        if (!alreadySorted) {
                          searchParams.set('sort_by', 'batch_number')
                          searchParams.set('sort_asc', false)
                        }
                        searchParams.set('business_id', business.id)
                        const finalParams = searchParams.toString()
                        return Promise.all([
                          getBorrowerDebtorPayments(finalParams),
                          getBorrowerDebtorPaymentsCount(finalParams),
                        ]).then((values) => ({
                          results: values[0],
                          total_results: values[1].count,
                        }))
                      },
                      requestMethod: 'GET',
                      columns: BusinessPaymentsTableColumns,
                      Component: ({ results, sort, handleSortChange, pageSize, columns }) => {
                        return (
                          <>
                            <BusinessPaymentsTable
                              columns={columns}
                              handleSortChange={handleSortChange}
                              pageSize={pageSize}
                              results={results}
                              sort={sort}
                            />
                          </>
                        )
                      },
                    },
                  ]}
                />
              </div>
              {/* Disabling for now as per Marco Capital's request https://app.asana.com/0/1188568317524329/1188536920916844
               */}
              <div label="Reports">
                <DownloadReportsTable business={business} pathProvided={true} reports={reports} />
              </div>
            </Tabs>
          </div>
        </section>
      </div>
    </S_ClientHomePage>
  )
}

function mapStateToProps(state) {
  const { env, user, business, deal, bank, featureFlags } = state

  return {
    tenant: env.tenant,
    user,
    business,
    deal: deal.deal,
    bank,
    featureFlags,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    getDeal: (businessId) => {
      dispatch(dealActions.getDeal(businessId))
    },
    showNotification: (notification) => {
      dispatch(notificationsActions.showNotification(notification))
    },
    updateBank: (userId, dealId) => {
      dispatch(bankActions.connectBank(bankConstants.VALUE_CONNECT, userId, dealId))
    },
  }
}

const connectedClientHomePage = connect(mapStateToProps, mapDispatchToProps)(ClientHomePage)
export { connectedClientHomePage as ClientHomePage }
